import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signup, verifyOtp, resendOtp } from "../common/api";
import { useAuth } from "../context/AuthContext";

export default function SignupPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [step, setStep] = useState("form");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resendCool, setResendCool] = useState(0);

  async function handleSignup() {
    setError(null);
    if (!email || !password) return setError("Please enter your email and password");
    if (password !== confirm) return setError("Passwords do not match");
    if (password.length < 8) return setError("Password must be at least 8 characters");

    setLoading(true);
    try {
      await signup(email, password);
      setStep("otp");
      startCooldown();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify() {
    setError(null);
    if (otp.length !== 6) return setError("Please enter the 6-digit verification code");

    setLoading(true);
    try {
      const { token } = await verifyOtp(email, otp);
      login(token);
      navigate("/");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setError(null);
    try {
      await resendOtp(email);
      startCooldown();
    } catch (err) {
      setError(err.message);
    }
  }

  function startCooldown() {
    setResendCool(60);
    const t = setInterval(() => {
      setResendCool((v) => {
        if (v <= 1) { clearInterval(t); return 0; }
        return v - 1;
      });
    }, 1000);
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-[420px]">

        {/* Logo */}
        <div
          className="text-center mb-8 cursor-pointer"
          onClick={() => navigate("/")}
        >
          <span className="text-[28px] font-bold text-black">ODK</span>
        </div>

        <div className="bg-white rounded-[24px] shadow-sm border border-gray-100 p-8">

          {/* Step 1: Sign up form */}
          {step === "form" && (
            <>
              <h1 className="text-[22px] font-bold text-gray-900 mb-1">Create Account</h1>
              <p className="text-[14px] text-gray-400 mb-6">Sign up with your email to get started</p>

              <div className="flex flex-col gap-4">
                <Field label="Email">
                  <input
                    type="email"
                    value={email}
                    placeholder="example@email.com"
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSignup()}
                    className={inputCls}
                  />
                </Field>

                <Field label="Password">
                  <input
                    type="password"
                    value={password}
                    placeholder="At least 8 characters"
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSignup()}
                    className={inputCls}
                  />
                </Field>

                <Field label="Confirm Password">
                  <input
                    type="password"
                    value={confirm}
                    placeholder="Re-enter your password"
                    onChange={(e) => setConfirm(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSignup()}
                    className={inputCls}
                  />
                </Field>

                {error && <p className="text-[13px] text-red-400">{error}</p>}

                <button
                  onClick={handleSignup}
                  disabled={loading}
                  className="w-full py-3 rounded-[12px] bg-[#E8876A] text-white font-semibold text-[15px] hover:bg-[#D96F52] transition-colors disabled:opacity-50 mt-1"
                >
                  {loading ? "Processing..." : "Send Verification Code"}
                </button>
              </div>

              <p className="text-center text-[13px] text-gray-400 mt-6">
                Already have an account?{" "}
                <Link to="/login" className="text-[#E8876A] font-semibold hover:underline">
                  Sign In
                </Link>
              </p>
            </>
          )}

          {/* Step 2: OTP verification */}
          {step === "otp" && (
            <>
              <button
                onClick={() => { setStep("form"); setError(null); setOtp(""); }}
                className="text-[13px] text-gray-400 hover:text-gray-600 mb-4 flex items-center gap-1"
              >
                ← Back
              </button>

              <h1 className="text-[22px] font-bold text-gray-900 mb-1">Verify Your Email</h1>
              <p className="text-[14px] text-gray-400 mb-6">
                We sent a 6-digit code to{" "}
                <span className="font-semibold text-gray-700">{email}</span>.<br />
                Please enter it below.
              </p>

              <div className="flex flex-col gap-4">
                <Field label="Verification Code">
                  <input
                    type="text"
                    value={otp}
                    placeholder="000000"
                    maxLength={6}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    onKeyDown={(e) => e.key === "Enter" && handleVerify()}
                    className={`${inputCls} text-center tracking-[0.3em] text-[20px] font-bold`}
                    autoFocus
                  />
                </Field>

                {error && <p className="text-[13px] text-red-400">{error}</p>}

                <button
                  onClick={handleVerify}
                  disabled={loading || otp.length !== 6}
                  className="w-full py-3 rounded-[12px] bg-[#E8876A] text-white font-semibold text-[15px] hover:bg-[#D96F52] transition-colors disabled:opacity-50 mt-1"
                >
                  {loading ? "Verifying..." : "Verify"}
                </button>

                <div className="text-center">
                  {resendCool > 0 ? (
                    <p className="text-[13px] text-gray-400">Resend available in {resendCool}s</p>
                  ) : (
                    <button
                      onClick={handleResend}
                      className="text-[13px] text-[#E8876A] hover:underline"
                    >
                      Resend Code
                    </button>
                  )}
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[13px] font-medium text-gray-600">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "px-4 py-2.5 rounded-[10px] border border-gray-200 text-[14px] text-gray-800 bg-white outline-none focus:border-[#E8876A] focus:ring-2 focus:ring-[#E8876A]/20 w-full transition-all";